import { ReactElement, useState, MouseEvent, useMemo, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useUserRole } from "../utils/hooks/useUserRole";
import { featureAccess } from "../permissions/access";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import HomeIcon from "@mui/icons-material/Home";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import DescriptionIcon from "@mui/icons-material/Description";
import ReceiptIcon from "@mui/icons-material/Receipt";
import StorefrontIcon from "@mui/icons-material/Storefront";
import BusinessIcon from "@mui/icons-material/Business";
import BakeryDiningIcon from "@mui/icons-material/BakeryDining";
import PriceCheckIcon from "@mui/icons-material/PriceCheck";
import HistoryIcon from "@mui/icons-material/History";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import AssignmentIcon from "@mui/icons-material/Assignment";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import TuneIcon from "@mui/icons-material/Tune";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import PaymentsIcon from "@mui/icons-material/Payments";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SettingsIcon from "@mui/icons-material/Settings";
import Badge from "@mui/material/Badge";
import { useQrDuplicateCount } from "../utils/hooks/useQrDuplicateCount";
import { useSaleDepositEditRequestCount } from "../utils/hooks/useSaleDepositEditRequestCount";
import { useExpiringPricePeriodCount } from "../utils/hooks/useExpiringPricePeriodCount";
import { COMPACT_QUERY, NARROW_QUERY } from "../utils/hooks/useCompact";
import { HEADER_HEIGHT, HEADER_HEIGHT_COMPACT } from "./Header";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  PRICE_PERIOD_PATH,
  PRODUCT_COST_PATH,
} from "./Product/shared/constants";
import { VISIT_FREQUENCY_PATH } from "./VisitFrequency/reportPaths";
import { COMPANY_SALES_MENU_TITLE, COMPANY_SALES_PATH } from "./CompanySalesReport/report";
import { PALLET_SUMMARY_PATH } from "./PalletSummary/constants";
import { WASTE_PERCENT_REPORT_PATH } from "./WastePercentReport/constants";
import {
  INCOME_EXPENSE_MENU_TITLE,
  INCOME_EXPENSE_REPORT_PATH,
} from "./IncomeExpenseReport/constants";
import { DEMAND_FACTOR_PATH } from "./DemandFactor/constants";
import {
  ROUTE_MANAGEMENT_PATH,
  ROUTE_MENU_TITLE,
} from "./RouteManagement/routeText";
import { BRANCH_LIST_PATH, BRANCH_MENU_TITLE } from "./Branch/constants";
import { VEHICLES_PATH } from "./Vehicle/vehiclePaths";
import { SYSTEM_SETTINGS_PATH } from "./SystemSettings/constants";
import { EXPRESS_DATA_PATH, EXPRESS_PATH } from "./ExpressData/constants";
import {
  CREDIT_DEBIT_NOTE_MENU_TITLE,
  CREDIT_DEBIT_NOTE_PATH,
} from "./CreditDebitNote/constants";
import { EXPRESS_DOC_TYPES, EXPRESS_DOC_TYPE_CONFIG } from "./ExpressDocument/constants";
import {
  EXPRESS_ACCOUNT_MENU_TITLE,
  EXPRESS_ACCOUNT_PATH,
  EXPRESS_CONTACT_MENU_TITLE,
  EXPRESS_CONTACT_PATH,
} from "./ExpressMaster/constants";
import {
  DAILY_EXPENSE_MENU_TITLE,
  DAILY_EXPENSE_PATH,
  DAILY_EXPENSE_REVIEW_MENU_TITLE,
  DAILY_EXPENSE_REVIEW_PATH,
  EXPENSE_MENU_TITLE,
  EXPENSE_PATH,
  MONTHLY_EXPENSE_CLOSE_MENU_TITLE,
  MONTHLY_EXPENSE_CLOSE_PATH,
  MONTH_ESTIMATE_MENU_TITLE,
  MONTH_ESTIMATE_PATH,
  ROUTE_SALES_TARGET_MENU_TITLE,
  ROUTE_SALES_TARGET_PATH,
} from "./DailyExpense/constants";
import {
  BDC_BILLINGS_MENU_TITLE,
  BDC_BILLINGS_PATH,
  BDC_CREDIT_NOTES_MENU_TITLE,
  BDC_CREDIT_NOTES_PATH,
  BDC_DOCUMENTS_MENU_TITLE,
  BDC_DOCUMENTS_PATH,
  BDC_EDI_MENU_TITLE,
  BDC_EDI_PATH,
  BDC_IMPORT_MENU_TITLE,
  BDC_IMPORT_PATH,
  BDC_INVOICES_MENU_TITLE,
  BDC_INVOICES_PATH,
  BDC_MENU_TITLE,
  BDC_PATH,
  BDC_QUEUE_MENU_TITLE,
  BDC_QUEUE_PATH,
  BDC_SETTINGS_MENU_TITLE,
  BDC_SETTINGS_PATH,
  BDC_STOCK_DAY_MENU_TITLE,
  BDC_DISPATCH_MENU_TITLE,
  BDC_DISPATCH_PATH,
  BDC_STOCK_DAY_PATH,
} from "./Bdc/constants";
import {
  BDC_DASHBOARD_MENU_TITLE,
  BDC_DASHBOARD_PATH,
  BDC_DASHBOARD_STORES_MENU_TITLE,
  BDC_DASHBOARD_STORES_PATH,
} from "./Bdc/Dashboard/constants";
import SidebarChildTree, {
  ChildMenuType,
  COMPACT_ICON_SIZE,
} from "./SidebarChildTree";

const compact = `@media ${COMPACT_QUERY}`;

/**
 * The menu entries that carry a live count.
 *
 * Matched on the title because that is what the menu tree is keyed by everywhere else here —
 * the permission filters above do the same. Kept as constants so the three places a menu is
 * rendered (expanded list, collapsed rail, and the pop-out) cannot drift apart.
 */
const QR_MENU_TITLE = "รายการ QR";
const DEPOSIT_REPORT_MENU_TITLE = "การโอน";

/**
 * The rest of รายงาน's gated children, named once for MENUS and the permission filter below
 * (which matches by Thai title, like PO โมเดิร์นเทรด). The labels leave out the word รายงาน —
 * the parent entry already says it.
 */
const PIVOT_MENU_TITLE = "Pivot";
const SALES_REPORT_EXPORT_MENU_TITLE = "การขายเฉพาะกลุ่ม";
const GB1_SALE_REPORT_MENU_TITLE = "GB1";
const WASTE_PERCENT_MENU_TITLE = "% ของเสีย";
const QR_DASHBOARD_MENU_TITLE = "แดชบอร์ด QR";
const VISIT_FREQUENCY_MENU_TITLE = "รอบเข้าเยี่ยมร้าน";
const FORMULA_SCORER_MENU_TITLE = "คะแนนสูตรลงของ";
const PALLET_SUMMARY_MENU_TITLE = "สรุปถาด";

/**
 * รายงาน outgrew a flat list, so its children sit under these headings. A heading is drawn
 * above the first child of its group that survives the permission filter, so a role that may
 * open none of a group's reports never sees the heading either. A child with no group would
 * sit under no heading: it is drawn as a plain row of รายงาน itself.
 */
const REPORT_GROUP = {
  sales: "ยอดขาย",
  finance: "การเงิน",
  routeRun: "การวิ่งสาย",
} as const;

/** The cost screen; hidden from everyone who may not read product costs. */
const PRODUCT_COST_MENU_TITLE = "ต้นทุนสินค้า";
/** Every book's price periods; same audience as the cost screen, badged with the expiring ones. */
const PRICE_PERIOD_MENU_TITLE = "ราคาตามช่วงเวลา";

/**
 * Express — the group ข้อมูล Express moved into from รายงาน, alongside the credit / debit notes
 * keyed against Express bills. Each child is gated on its own key and the group is hidden when
 * neither survives.
 */
const EXPRESS_MENU_TITLE = "Express";
const EXPRESS_DATA_MENU_TITLE = "ข้อมูล Express";
/** ซื้อเชื่อ, ซื้อสด, รายได้อื่นๆ and รับชำระหนี้, in Express's own order. */
const EXPRESS_DOCUMENT_MENUS = EXPRESS_DOC_TYPES.map((docType) => ({
  title: EXPRESS_DOC_TYPE_CONFIG[docType].menuTitle,
  link: EXPRESS_DOC_TYPE_CONFIG[docType].path,
}));
const EXPRESS_DOCUMENT_MENU_TITLES = EXPRESS_DOCUMENT_MENUS.map((menu) => menu.title);
const EXPRESS_MASTER_MENU_TITLES = [EXPRESS_CONTACT_MENU_TITLE, EXPRESS_ACCOUNT_MENU_TITLE];

/** Company-wide settings; MASTER only. */
const SYSTEM_SETTINGS_MENU_TITLE = "ตั้งค่าระบบ";

/**
 * PO โมเดิร์นเทรด. The permission filters below match a menu entry by its Thai title, so the
 * group and its three children are named once here and reused by both MENUS and the filters:
 * a typo in one of the two would silently drop the gate instead of failing.
 */
const PO_MENU_TITLE = "PO โมเดิร์นเทรด";
const PO_LIST_MENU_TITLE = "รายการ PO";
const PO_IMPORT_MENU_TITLE = "นำเข้าไฟล์ PO";
const PO_BRANCH_MENU_TITLE = "PO ของสาขา";

/** จัดการสาขา and its vehicle child, matched by title like every other gated entry. */
const BRANCH_MANAGE_MENU_TITLE = "จัดการสาขา";
const VEHICLE_MENU_TITLE = "รถ";

/** ออเดอร์พิเศษ — orders the back office books for a customer (menu matched by title). */
const SPECIAL_ORDER_MENU_TITLE = "ออเดอร์พิเศษ";

/**
 * Demand Factor — its own entry rather than a child of จัดการสาขา: that group is shown on the
 * stock-upload key alone, which head office does not hold.
 */
const DEMAND_FACTOR_MENU_TITLE = "Demand Factor";

/**
 * A count that means something runs out: a QR paid more than once, which SCB will only take
 * back on the Bangkok day it arrived, a branch's request to reopen a closed deposit that
 * expires seven days after it was raised, or a price period that ends within the week and
 * falls back to its next price unless someone extends it. `error` rather than `warning` — it
 * is not "look at this when you get a moment", it has a deadline.
 *
 * Rendered as nothing at all when the count is zero, which is almost always: no wrapper, so
 * nothing shifts the icon it rides on. A badge that is usually showing something is a badge
 * nobody reads.
 */
function CountBadge({ count, children }: Readonly<{ count: number; children: ReactElement }>) {
  if (count <= 0) return children;
  return (
    <Badge
      badgeContent={count}
      color="error"
      overlap="circular"
      sx={{ "& .MuiBadge-badge": { fontWeight: 700 } }}
    >
      {children}
    </Badge>
  );
}

export const SIDEBAR_WIDTH_EXPANDED = 240;
export const SIDEBAR_WIDTH_COLLAPSED = 72;

/** A menu row: icon-only and centred on the collapsed rail, icon and label otherwise. */
const rowSxFor = (isRail: boolean) => ({
  borderRadius: "10px",
  minHeight: "48px",
  mb: 0.5,
  px: isRail ? 1 : 1.5,
  py: 1,
  justifyContent: isRail ? "center" : "flex-start",
  color: "text.secondary",
  "& .MuiListItemText-primary": { fontSize: "0.95rem", fontWeight: 500 },
  [compact]: {
    minHeight: "40px",
    mb: 0.25,
    py: 0.5,
    "& .MuiTypography-root": { fontSize: "0.85rem" },
  },
});

const iconSxFor = (isRail: boolean) => ({
  minWidth: 0,
  mr: isRail ? 0 : 2,
  justifyContent: "center",
  color: "text.secondary",
  "& .MuiSvgIcon-root": {
    [compact]: { fontSize: COMPACT_ICON_SIZE },
  },
});

/** The pop-out is a slice of the expanded drawer: same width, same inset as its list. */
const flyoutPaperSx = { width: SIDEBAR_WIDTH_EXPANDED, p: 1 };

/** The pop-out's title row: the parent's icon and name, where a drawer parent row has them. */
const flyoutTitleSx = { minHeight: "48px", px: 1.5 };
const flyoutTitleTextSx = { fontSize: "0.95rem", fontWeight: 600, color: "text.primary" };

const STORAGE_KEY = "gb_sidebar_collapsed";

type MenuItemType = {
  title: string;
  link: string;
  icon: ReactElement;
  children?: ChildMenuType[];
};

const MENUS: MenuItemType[] = [
  {
    title: "หน้าหลัก",
    link: "/",
    icon: <HomeIcon />,
  },
  {
    title: "การขาย",
    link: "/sales",
    icon: <ReceiptIcon />,
    children: [
      { title: "รายการขาย", link: "/sales" },
      { title: "คำขอพิมพ์ซ้ำ", link: "/sales/reprint-requests" },
      { title: "สิทธิ์พิมพ์ซ้ำ", link: "/sales/reprint-authorization" },
    ],
  },
  {
    title: EXPRESS_MENU_TITLE,
    link: EXPRESS_PATH,
    icon: <RequestQuoteIcon />,
    children: [
      { title: EXPRESS_DATA_MENU_TITLE, link: EXPRESS_DATA_PATH },
      { title: CREDIT_DEBIT_NOTE_MENU_TITLE, link: CREDIT_DEBIT_NOTE_PATH },
      ...EXPRESS_DOCUMENT_MENUS,
      { title: EXPRESS_CONTACT_MENU_TITLE, link: EXPRESS_CONTACT_PATH },
      { title: EXPRESS_ACCOUNT_MENU_TITLE, link: EXPRESS_ACCOUNT_PATH },
    ],
  },
  {
    title: SPECIAL_ORDER_MENU_TITLE,
    link: "/special-orders",
    icon: <LocalShippingIcon />,
  },
  {
    title: "ลูกค้า",
    link: "/customer",
    icon: <StorefrontIcon />,
  },
  {
    title: ROUTE_MENU_TITLE,
    link: ROUTE_MANAGEMENT_PATH,
    icon: <AltRouteIcon />,
  },
  {
    title: BRANCH_MENU_TITLE,
    link: BRANCH_LIST_PATH,
    icon: <BusinessIcon />,
  },
  {
    title: "สินค้า",
    link: "/product",
    icon: <BakeryDiningIcon />,
    children: [
      { title: "รายการสินค้า", link: "/product/products" },
      { title: "สมุดราคา", link: "/product/price-book" },
      { title: PRODUCT_COST_MENU_TITLE, link: PRODUCT_COST_PATH },
      { title: PRICE_PERIOD_MENU_TITLE, link: PRICE_PERIOD_PATH },
    ],
  },
  {
    title: "รายงาน",
    link: "/report",
    icon: <DescriptionIcon />,
    children: [
      { title: "การขายรายวัน", link: "/report/sale", group: REPORT_GROUP.sales },
      { title: PIVOT_MENU_TITLE, link: "/report/pivot", group: REPORT_GROUP.sales },
      {
        title: SALES_REPORT_EXPORT_MENU_TITLE,
        link: "/report/sales-report",
        group: REPORT_GROUP.sales,
      },
      {
        title: GB1_SALE_REPORT_MENU_TITLE,
        link: "/report/gb1-sale-report",
        group: REPORT_GROUP.sales,
      },
      {
        title: WASTE_PERCENT_MENU_TITLE,
        link: WASTE_PERCENT_REPORT_PATH,
        group: REPORT_GROUP.sales,
      },
      {
        title: COMPANY_SALES_MENU_TITLE,
        link: COMPANY_SALES_PATH,
        group: REPORT_GROUP.sales,
      },
      {
        title: DEPOSIT_REPORT_MENU_TITLE,
        link: "/report/deposit",
        group: REPORT_GROUP.finance,
      },
      {
        title: INCOME_EXPENSE_MENU_TITLE,
        link: INCOME_EXPENSE_REPORT_PATH,
        group: REPORT_GROUP.finance,
      },
      {
        title: QR_MENU_TITLE,
        link: "/report/qr-transaction",
        group: REPORT_GROUP.finance,
      },
      {
        title: QR_DASHBOARD_MENU_TITLE,
        link: "/report/qr-dashboard",
        group: REPORT_GROUP.finance,
      },
      { title: "การหักเงิน", link: "/report/deduction", group: REPORT_GROUP.finance },
      {
        title: VISIT_FREQUENCY_MENU_TITLE,
        link: VISIT_FREQUENCY_PATH,
        group: REPORT_GROUP.routeRun,
      },
      {
        title: FORMULA_SCORER_MENU_TITLE,
        link: "/report/formula-scorer",
        group: REPORT_GROUP.routeRun,
      },
      {
        title: PALLET_SUMMARY_MENU_TITLE,
        link: PALLET_SUMMARY_PATH,
        group: REPORT_GROUP.routeRun,
      },
    ],
  },
  {
    title: EXPENSE_MENU_TITLE,
    link: EXPENSE_PATH,
    icon: <PaymentsIcon />,
    children: [
      { title: DAILY_EXPENSE_MENU_TITLE, link: DAILY_EXPENSE_PATH },
      { title: DAILY_EXPENSE_REVIEW_MENU_TITLE, link: DAILY_EXPENSE_REVIEW_PATH },
      { title: MONTHLY_EXPENSE_CLOSE_MENU_TITLE, link: MONTHLY_EXPENSE_CLOSE_PATH },
      { title: MONTH_ESTIMATE_MENU_TITLE, link: MONTH_ESTIMATE_PATH },
      { title: ROUTE_SALES_TARGET_MENU_TITLE, link: ROUTE_SALES_TARGET_PATH },
    ],
  },
  {
    title: BRANCH_MANAGE_MENU_TITLE,
    link: "/branch",
    icon: <Inventory2Icon />,
    children: [
      { title: "อัปโหลดสต๊อก", link: "/branch/stock/upload" },
      { title: "อัปโหลดแผนวิ่ง", link: "/branch/pathway/upload" },
      { title: VEHICLE_MENU_TITLE, link: VEHICLES_PATH },
    ],
  },
  {
    title: DEMAND_FACTOR_MENU_TITLE,
    link: DEMAND_FACTOR_PATH,
    icon: <TuneIcon />,
  },
  {
    title: PO_MENU_TITLE,
    link: "/purchase-order",
    icon: <AssignmentIcon />,
    children: [
      { title: PO_LIST_MENU_TITLE, link: "/purchase-order" },
      { title: PO_IMPORT_MENU_TITLE, link: "/purchase-order/import" },
      { title: PO_BRANCH_MENU_TITLE, link: "/purchase-order/branch" },
    ],
  },
  {
    title: BDC_MENU_TITLE,
    link: BDC_PATH,
    icon: <WarehouseIcon />,
    children: [
      { title: BDC_QUEUE_MENU_TITLE, link: BDC_QUEUE_PATH },
      { title: BDC_IMPORT_MENU_TITLE, link: BDC_IMPORT_PATH },
      { title: BDC_DOCUMENTS_MENU_TITLE, link: BDC_DOCUMENTS_PATH },
      { title: BDC_EDI_MENU_TITLE, link: BDC_EDI_PATH },
      { title: BDC_INVOICES_MENU_TITLE, link: BDC_INVOICES_PATH },
      { title: BDC_CREDIT_NOTES_MENU_TITLE, link: BDC_CREDIT_NOTES_PATH },
      { title: BDC_BILLINGS_MENU_TITLE, link: BDC_BILLINGS_PATH },
      { title: BDC_DISPATCH_MENU_TITLE, link: BDC_DISPATCH_PATH },
      { title: BDC_STOCK_DAY_MENU_TITLE, link: BDC_STOCK_DAY_PATH },
      { title: BDC_DASHBOARD_MENU_TITLE, link: BDC_DASHBOARD_PATH },
      { title: BDC_DASHBOARD_STORES_MENU_TITLE, link: BDC_DASHBOARD_STORES_PATH },
      { title: BDC_SETTINGS_MENU_TITLE, link: BDC_SETTINGS_PATH },
    ],
  },
  {
    title: "E-tax",
    link: "/e-tax",
    icon: <PriceCheckIcon />,
  },
  {
    title: "ประวัติการทำรายการ",
    link: "/event-logs",
    icon: <HistoryIcon />,
  },
  {
    title: "ตั้งค่าผู้ใช้",
    link: "/user-management",
    icon: <AccountCircleIcon />,
  },
  {
    title: SYSTEM_SETTINGS_MENU_TITLE,
    link: SYSTEM_SETTINGS_PATH,
    icon: <SettingsIcon />,
  },
];

export default function Sidebar() {
  const location = useLocation();
  const role = useUserRole();
  // Money sitting on a QR that was paid twice. Polled here rather than on the QR screen,
  // because the whole point is to be seen from wherever someone happens to be working.
  const duplicateCount = useQrDuplicateCount();
  // Requests waiting for the supervisor to answer. On the sidebar rather than only on the tab
  // they live in, because a badge is how somebody learns there is work waiting WITHOUT having
  // to open the screen — inside that screen it is only ever seen by someone already there.
  const pendingEditRequestCount = useSaleDepositEditRequestCount();
  // Price periods about to end: marketing has to extend them or let the line fall back to its
  // next price, and the badge is what tells them without opening the page.
  const expiringPeriodCount = useExpiringPricePeriodCount();
  const isNarrow = useMediaQuery(NARROW_QUERY, { noSsr: true });

  // Manual collapse toggle, persisted to localStorage. Default: expanded —
  // unless on a narrow viewport (NARROW_QUERY), where we auto-default collapsed.
  // A persisted value always wins so manual overrides survive a refresh.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null) return stored === "true";
    return isNarrow;
  });

  // If the viewport becomes narrow and the user has never set a preference,
  // auto-collapse. Once the user toggles (preference stored) this no longer fires.
  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY) === null) {
      setCollapsed(isNarrow);
    }
  }, [isNarrow]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEY, String(next));
      return next;
    });
  };

  // Collapsed-mode flyout (mirrors Navbar's anchored Menu pattern exactly).
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [flyoutMenu, setFlyoutMenu] = useState<MenuItemType | null>(null);

  const handleFlyoutClick = (
    event: MouseEvent<HTMLElement>,
    menu: MenuItemType,
  ) => {
    if (menu.children) {
      setMenuAnchor(event.currentTarget);
      setFlyoutMenu(menu);
    } else {
      setMenuAnchor(null);
      setFlyoutMenu(null);
    }
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setFlyoutMenu(null);
  };

  // ── EXACT permission-filter block migrated from Navbar.tsx (byte-for-byte) ──
  const menus = useMemo(
    () =>
      MENUS.map((menu) => {
        // "การขาย" grouping: hide the Reprint Authorization child for non-approvers.
        if (menu.title === "การขาย") {
          return {
            ...menu,
            children: menu.children?.filter((child) => {
              if (
                child.title === "สิทธิ์พิมพ์ซ้ำ" &&
                !featureAccess.reprintAuth(role)
              ) {
                return false;
              }
              if (
                child.title === "คำขอพิมพ์ซ้ำ" &&
                !featureAccess.reprintRequest(role)
              ) {
                return false;
              }
              return true;
            }),
          };
        }

        if (menu.title === EXPRESS_MENU_TITLE) {
          return {
            ...menu,
            children: menu.children?.filter((child) => {
              if (
                child.title === EXPRESS_DATA_MENU_TITLE &&
                !featureAccess.expressData(role)
              ) {
                return false;
              }
              if (
                child.title === CREDIT_DEBIT_NOTE_MENU_TITLE &&
                !featureAccess.creditDebitNote(role)
              ) {
                return false;
              }
              if (
                EXPRESS_DOCUMENT_MENU_TITLES.includes(child.title) &&
                !featureAccess.expressDocument(role)
              ) {
                return false;
              }
              if (
                EXPRESS_MASTER_MENU_TITLES.includes(child.title) &&
                !featureAccess.expressMaster(role)
              ) {
                return false;
              }
              return true;
            }),
          };
        }

        // ค่าใช้จ่าย: the branch keys its days, head office approves them and closes the month —
        // each child on its key.
        if (menu.title === EXPENSE_MENU_TITLE) {
          return {
            ...menu,
            children: menu.children?.filter(
              (child) =>
                (child.title !== DAILY_EXPENSE_MENU_TITLE || featureAccess.dailyExpense(role)) &&
                (child.title !== DAILY_EXPENSE_REVIEW_MENU_TITLE ||
                  featureAccess.dailyExpenseReview(role)) &&
                (child.title !== MONTHLY_EXPENSE_CLOSE_MENU_TITLE ||
                  featureAccess.monthlyExpenseClose(role)) &&
                (child.title !== MONTH_ESTIMATE_MENU_TITLE || featureAccess.monthEstimate(role)) &&
                (child.title !== ROUTE_SALES_TARGET_MENU_TITLE ||
                  featureAccess.routeSalesTarget(role)),
            ),
          };
        }

        // ใบวางบิล is head-office accounting + MASTER only; นำเข้าไฟล์ is the BDC desk's
        // (`bdcDesk`, head office reads); the rest of BDC follows `bdc`.
        if (menu.title === BDC_MENU_TITLE) {
          return {
            ...menu,
            children: menu.children?.filter(
              (child) =>
                (child.title !== BDC_BILLINGS_MENU_TITLE || featureAccess.bdcBilling(role)) &&
                (child.title !== BDC_IMPORT_MENU_TITLE || featureAccess.bdcDesk(role)),
            ),
          };
        }

        if (menu.title === BRANCH_MANAGE_MENU_TITLE) {
          return {
            ...menu,
            children: menu.children?.filter(
              (child) =>
                child.title !== VEHICLE_MENU_TITLE ||
                featureAccess.vehicleManagement(role),
            ),
          };
        }

        if (menu.title === "สินค้า") {
          return {
            ...menu,
            children: menu.children?.filter((child) => {
              if (child.title === PRODUCT_COST_MENU_TITLE) {
                return featureAccess.productCostRead(role);
              }
              if (child.title === PRICE_PERIOD_MENU_TITLE) {
                return featureAccess.pricePeriodRead(role);
              }
              return true;
            }),
          };
        }

        // Two audiences share one group: head office imports the file and works the
        // all-branch list, the branch only reads and matches its own POs. Each child
        // carries the key of the endpoints it calls, so a visible entry can never land
        // on a 403.
        if (menu.title === PO_MENU_TITLE) {
          return {
            ...menu,
            children: menu.children?.filter((child) => {
              if (
                (child.title === PO_LIST_MENU_TITLE ||
                  child.title === PO_IMPORT_MENU_TITLE) &&
                !featureAccess.poManage(role)
              ) {
                return false;
              }
              if (
                child.title === PO_BRANCH_MENU_TITLE &&
                !featureAccess.poBranch(role)
              ) {
                return false;
              }
              return true;
            }),
          };
        }

        if (menu.title !== "รายงาน") return menu;

        return {
          ...menu,
          children: menu.children?.filter((child) => {
            if (
              child.title === SALES_REPORT_EXPORT_MENU_TITLE &&
              !featureAccess.salesReportExport(role)
            ) {
              return false;
            }
            if (
              child.title === GB1_SALE_REPORT_MENU_TITLE &&
              !featureAccess.gb1SaleReport(role)
            ) {
              return false;
            }
            if (
              child.title === WASTE_PERCENT_MENU_TITLE &&
              !featureAccess.wastePercentReport(role)
            ) {
              return false;
            }
            if (
              child.title === COMPANY_SALES_MENU_TITLE &&
              !featureAccess.companySalesReport(role)
            ) {
              return false;
            }
            if (
              child.title === DEPOSIT_REPORT_MENU_TITLE &&
              !featureAccess.saleDepositReport(role)
            ) {
              return false;
            }
            if (
              child.title === INCOME_EXPENSE_MENU_TITLE &&
              !featureAccess.incomeExpenseReport(role)
            ) {
              return false;
            }
            if (
              child.title === QR_MENU_TITLE &&
              !featureAccess.qrTransaction(role)
            ) {
              return false;
            }
            if (
              child.title === QR_DASHBOARD_MENU_TITLE &&
              !featureAccess.qrAnalytics(role)
            ) {
              return false;
            }
            if (
              child.title === VISIT_FREQUENCY_MENU_TITLE &&
              !featureAccess.visitFrequency(role)
            ) {
              return false;
            }
            if (
              child.title === FORMULA_SCORER_MENU_TITLE &&
              !featureAccess.formulaScorer(role)
            ) {
              return false;
            }
            if (
              child.title === PALLET_SUMMARY_MENU_TITLE &&
              !featureAccess.palletSummary(role)
            ) {
              return false;
            }
            if (child.title === PIVOT_MENU_TITLE && !featureAccess.pivot(role)) {
              return false;
            }
            return true;
          }),
        };
      }).filter((menu) => {
        // A BDC-only user (bdc manager / bdc operator and no other role) sees the BDC group
        // and nothing else — not หน้าหลัก, nor the menus below that carry no permission key.
        if (role.isBdcOnly) return menu.title === BDC_MENU_TITLE;
        if (
          menu.title === "ตั้งค่าผู้ใช้" &&
          !featureAccess.userManagement(role)
        ) {
          return false;
        }
        if (
          menu.title === SYSTEM_SETTINGS_MENU_TITLE &&
          !featureAccess.systemSettings(role)
        ) {
          return false;
        }
        if (menu.title === "E-tax" && !featureAccess.eTax(role)) {
          return false;
        }
        if (
          menu.title === SPECIAL_ORDER_MENU_TITLE &&
          !featureAccess.specialOrder(role)
        ) {
          return false;
        }
        if (
          menu.title === BRANCH_MANAGE_MENU_TITLE &&
          !featureAccess.stockManagement(role)
        ) {
          return false;
        }
        if (menu.title === "สินค้า" && !featureAccess.product(role)) {
          return false;
        }
        if (
          menu.title === DEMAND_FACTOR_MENU_TITLE &&
          !featureAccess.demandFactor(role)
        ) {
          return false;
        }
        if (
          menu.title === ROUTE_MENU_TITLE &&
          !featureAccess.routeManagement(role)
        ) {
          return false;
        }
        if (
          menu.title === BRANCH_MENU_TITLE &&
          !featureAccess.branchManagement(role)
        ) {
          return false;
        }
        if (menu.title === BDC_MENU_TITLE && !featureAccess.bdc(role)) {
          return false;
        }
        // Express carries no key of its own: it is shown while any child is.
        if (menu.title === EXPRESS_MENU_TITLE && !menu.children?.length) {
          return false;
        }
        // Nor does ค่าใช้จ่าย: it is shown while one of its children is.
        if (menu.title === EXPENSE_MENU_TITLE && !menu.children?.length) {
          return false;
        }
        // Either key keeps the group: gating it on one alone would hide the whole of
        // PO โมเดิร์นเทรด from the other audience even though its child is allowed.
        if (
          menu.title === PO_MENU_TITLE &&
          !featureAccess.poManage(role) &&
          !featureAccess.poBranch(role)
        ) {
          return false;
        }
        return true;
      }),
    [role],
  );

  /** What each badged child currently carries; every other menu title carries nothing. */
  const countByChildTitle: Record<string, number> = useMemo(
    () => ({
      [QR_MENU_TITLE]: duplicateCount,
      [DEPOSIT_REPORT_MENU_TITLE]: pendingEditRequestCount,
      [PRICE_PERIOD_MENU_TITLE]: expiringPeriodCount,
    }),
    [duplicateCount, pendingEditRequestCount, expiringPeriodCount],
  );

  const countForChild = (child: ChildMenuType): number =>
    countByChildTitle[child.title] ?? 0;

  /**
   * The count to show on a PARENT menu entry: the sum of what its children carry.
   *
   * Written as a lookup over the children rather than as "is this the reports menu" so that
   * moving a badged child under a different group, or badging a third one later, needs no
   * change here.
   */
  const countForMenu = (menu: MenuItemType): number =>
    menu.children?.reduce((total, child) => total + countForChild(child), 0) ?? 0;

  // Active predicate (reused from Navbar's currentTab logic).
  // Matched on whole path segments, so "/branch" (จัดการสาขา) stays unlit on "/branches".
  const isMenuActive = (menu: MenuItemType) =>
    menu.link === "/"
      ? location.pathname === "/"
      : location.pathname === menu.link ||
        location.pathname.startsWith(`${menu.link}/`);

  const [openParents, setOpenParents] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setOpenParents((prev) => {
      let changed = false;
      const next = { ...prev };
      MENUS.forEach((menu) => {
        const childActive = menu.children?.some((child) =>
          location.pathname.startsWith(child.link),
        );
        if (childActive && !next[menu.title]) {
          next[menu.title] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [location.pathname]);

  const toggleParent = (title: string) =>
    setOpenParents((prev) => ({ ...prev, [title]: !prev[title] }));

  const width = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;

  // Publish the live sidebar width as a global CSS variable so viewport-fixed
  // elements (e.g. the report details footer bar) can offset themselves by it
  // and stay aligned with the main content area as the sidebar collapses.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--gb-sidebar-width", `${width}px`);
    return () => {
      root.style.setProperty("--gb-sidebar-width", "0px");
    };
  }, [width]);

  const activeSx = {
    bgcolor: (theme: import("@mui/material/styles").Theme) =>
      theme.palette.primary.main + "1A",
    color: "primary.main",
    "& .MuiListItemIcon-root": { color: "primary.main" },
    "&:hover": {
      bgcolor: (theme: import("@mui/material/styles").Theme) =>
        theme.palette.primary.main + "26",
    },
  };

  const rowSx = rowSxFor(collapsed);
  const iconSx = iconSxFor(collapsed);
  // The pop-out shows a parent opened as in the expanded drawer, whatever the rail's state.
  const drawerRowSx = rowSxFor(false);
  const drawerIconSx = iconSxFor(false);

  return (
    <Drawer
      variant="permanent"
      sx={{
        width,
        flexShrink: 0,
        whiteSpace: "nowrap",
        "& .MuiDrawer-paper": {
          width,
          // Override the global MuiPaper 16px padding so the sidebar content
          // (toggle + List) controls its own insets and is not double-padded.
          padding: 0,
          boxSizing: "border-box",
          // Full-height sidebar: sticks flush beneath the top header and spans
          // the whole viewport so it never reads as a short floating card.
          position: "sticky",
          top: 0,
          height: `calc(100vh - ${HEADER_HEIGHT}px)`,
          [compact]: { height: `calc(100vh - ${HEADER_HEIGHT_COMPACT}px)` },
          border: "none",
          borderRight: (theme) => `1px solid ${theme.palette.divider}`,
          bgcolor: "#fff",
          overflowX: "hidden",
          overflowY: "auto",
          boxShadow: "4px 0px 12px -6px rgba(0,0,0,0.12)",
        },
      }}
    >
      {/* Collapse / expand toggle */}
      <Box
        sx={{
          display: "flex",
          justifyContent: collapsed ? "center" : "flex-end",
          alignItems: "center",
          px: 1,
          py: 1,
          [compact]: { py: 0.5 },
        }}
      >
        <Tooltip
          title={collapsed ? "ขยายเมนู" : "ย่อเมนู"}
          placement="right"
        >
          <IconButton size="small" onClick={toggleCollapsed}>
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      <List sx={{ px: 1, py: 0 }}>
        {menus.map((menu) => {
          const active = isMenuActive(menu);

          // ── Parent item (has children) ──
          if (menu.children) {
            const parentOpen = Boolean(openParents[menu.title]);

            // Collapsed: icon-only button that opens the anchored flyout.
            if (collapsed) {
              return (
                <Tooltip
                  key={menu.title}
                  title={menu.title}
                  placement="right"
                >
                  <ListItemButton
                    sx={{ ...rowSx, ...(active ? activeSx : {}) }}
                    onClick={(e) => handleFlyoutClick(e, menu)}
                  >
                    {/* On the collapsed rail no child is on screen, so the parent has to
                        carry the child's count or a collapsed sidebar would hide the one
                        thing this badge exists to show. */}
                    <ListItemIcon sx={iconSx}>
                      <CountBadge count={countForMenu(menu)}>{menu.icon}</CountBadge>
                    </ListItemIcon>
                  </ListItemButton>
                </Tooltip>
              );
            }

            // Expanded: inline accordion of children.
            return (
              <Box key={menu.title}>
                <ListItemButton
                  sx={{ ...rowSx, ...(active ? activeSx : {}) }}
                  onClick={() => toggleParent(menu.title)}
                >
                  {/* The accordion starts closed, so without this the count would only
                      appear to someone who had already opened the group it lives in. */}
                  <ListItemIcon sx={iconSx}>
                    <CountBadge count={parentOpen ? 0 : countForMenu(menu)}>
                      {menu.icon}
                    </CountBadge>
                  </ListItemIcon>
                  <ListItemText primary={menu.title} />
                  {parentOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={parentOpen} timeout="auto" unmountOnExit>
                  <SidebarChildTree
                    items={menu.children}
                    rowSx={rowSx}
                    activeSx={activeSx}
                    countForChild={countForChild}
                  />
                </Collapse>
              </Box>
            );
          }

          // ── Leaf item ──
          const leaf = (
            <ListItemButton
              key={menu.title}
              component={Link}
              to={menu.link}
              selected={active}
              sx={{ ...rowSx, ...(active ? activeSx : {}) }}
              onClick={handleCloseMenu}
            >
              <ListItemIcon sx={iconSx}>{menu.icon}</ListItemIcon>
              {!collapsed && <ListItemText primary={menu.title} />}
            </ListItemButton>
          );

          return collapsed ? (
            <Tooltip key={menu.title} title={menu.title} placement="right">
              {leaf}
            </Tooltip>
          ) : (
            leaf
          );
        })}
      </List>

      {/* Collapsed rail: a parent's pop-out is that parent opened as in the expanded drawer —
          its title on top and its tree hanging from it. */}
      <Popover
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: flyoutPaperSx } }}
      >
        {flyoutMenu?.children && (
          <>
            <Stack direction="row" alignItems="center" sx={flyoutTitleSx}>
              <ListItemIcon sx={drawerIconSx}>{flyoutMenu.icon}</ListItemIcon>
              <Typography sx={flyoutTitleTextSx}>{flyoutMenu.title}</Typography>
            </Stack>
            <SidebarChildTree
              items={flyoutMenu.children}
              rowSx={drawerRowSx}
              activeSx={activeSx}
              countForChild={countForChild}
              onNavigate={handleCloseMenu}
            />
          </>
        )}
      </Popover>
    </Drawer>
  );
}
